/**
 * A read-only view over decoded fetch chunks, exposing the same `length` /
 * `indexOf` / `charCodeAt` / `slice` surface `landxmlTin.ts`'s scanners use on
 * a plain `string` — without ever concatenating the chunks into one.
 *
 * WHY THIS EXISTS
 * ----------------
 * V8 refuses to allocate a string longer than ~1.07 GB
 * (`RangeError: Invalid string length`). A LandXML DTM export can comfortably
 * exceed that — a 1.2 GB file failed exactly there, at `parts.join('')`,
 * before a single byte of it was parsed (see `LandXML.tsx`).
 *
 * `landxmlTin.ts`'s scanners were never the problem: they walk the text with
 * `indexOf`/`charCodeAt` and only ever materialise tiny slices out of the huge
 * `<Pnts>`/`<Faces>` blocks (a number token, an id attribute, a `<Units>`
 * body). Only the eager, one-shot concatenation of the whole document into a
 * single string was. `ChunkedText` keeps the decoder's fetch chunks as-is and
 * answers scans against them directly, so `landxmlTin.ts` never needs a
 * change beyond its parameter type — a plain `string` already has this same
 * method surface, so every existing string-based call site and test keeps
 * working unchanged.
 */
export interface TextLike {
  readonly length: number;
  indexOf(needle: string, from?: number): number;
  charCodeAt(index: number): number;
  slice(start: number, end?: number): string;
}

export class ChunkedText implements TextLike {
  private readonly chunks: readonly string[];
  /** chunkStarts[i] = global offset of chunks[i][0]; one trailing entry = total length. */
  private readonly chunkStarts: Float64Array;
  readonly length: number;
  /** Index into `chunks` last used — scanning is overwhelmingly sequential. */
  private cachedChunk = 0;

  constructor(chunks: readonly string[]) {
    this.chunks = chunks;
    const starts = new Float64Array(chunks.length + 1);
    let total = 0;
    for (let i = 0; i < chunks.length; i++) {
      starts[i] = total;
      total += chunks[i].length;
    }
    starts[chunks.length] = total;
    this.chunkStarts = starts;
    this.length = total;
  }

  /** Index of the chunk containing global offset `pos` (0 <= pos < length). */
  private chunkAt(pos: number): number {
    const n = this.chunks.length;
    let c = this.cachedChunk < n ? this.cachedChunk : n - 1;
    if (pos >= this.chunkStarts[c] && pos < this.chunkStarts[c + 1]) return c;
    if (pos >= this.chunkStarts[c]) {
      while (c + 1 < n && pos >= this.chunkStarts[c + 1]) c++;
      return (this.cachedChunk = c);
    }
    while (c > 0 && pos < this.chunkStarts[c]) c--;
    if (pos >= this.chunkStarts[c] && pos < this.chunkStarts[c + 1]) return (this.cachedChunk = c);
    // Big jump backwards past the linear walk's reach — binary search.
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.chunkStarts[mid] <= pos) lo = mid;
      else hi = mid - 1;
    }
    return (this.cachedChunk = lo);
  }

  charCodeAt(index: number): number {
    if (index < 0 || index >= this.length || this.chunks.length === 0) return NaN;
    const c = this.chunkAt(index);
    return this.chunks[c].charCodeAt(index - this.chunkStarts[c]);
  }

  slice(start: number, end: number = this.length): string {
    if (this.chunks.length === 0) return '';
    const from = Math.max(0, Math.min(start, this.length));
    const to = Math.max(from, Math.min(end, this.length));
    if (to <= from) return '';
    const c0 = this.chunkAt(from);
    if (to <= this.chunkStarts[c0 + 1]) {
      return this.chunks[c0].slice(from - this.chunkStarts[c0], to - this.chunkStarts[c0]);
    }
    const parts: string[] = [];
    let c = c0;
    let pos = from;
    while (pos < to) {
      const chunkEnd = this.chunkStarts[c + 1];
      const localEnd = Math.min(to, chunkEnd) - this.chunkStarts[c];
      parts.push(this.chunks[c].slice(pos - this.chunkStarts[c], localEnd));
      pos = Math.min(to, chunkEnd);
      c++;
    }
    return parts.join('');
  }

  indexOf(needle: string, from = 0): number {
    if (this.chunks.length === 0) return needle.length === 0 ? 0 : -1;
    if (needle.length === 0) return Math.max(0, Math.min(from, this.length));
    let pos = Math.max(0, from);
    if (pos >= this.length) return -1;
    let c = this.chunkAt(pos);
    const n = this.chunks.length;
    while (c < n) {
      const chunkStart = this.chunkStarts[c];
      const local = this.chunks[c].indexOf(needle, pos - chunkStart);
      if (local >= 0) return chunkStart + local;
      // The needle may straddle this chunk's tail and the next chunk's head —
      // check a small boundary window rather than re-scanning whole chunks.
      if (c + 1 < n) {
        const overlap = needle.length - 1;
        const windowStart = Math.max(pos, this.chunkStarts[c + 1] - overlap);
        const windowEnd = Math.min(this.length, this.chunkStarts[c + 1] + overlap);
        if (windowEnd > windowStart) {
          const boundary = this.slice(windowStart, windowEnd);
          const hit = boundary.indexOf(needle);
          if (hit >= 0) return windowStart + hit;
        }
      }
      pos = this.chunkStarts[c + 1];
      c++;
    }
    return -1;
  }
}
